import { Box, Divider, Grid } from '@material-ui/core'
import React, { ReactElement } from 'react'
import {
  EditButton,
  FunctionField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  TitleProps,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { EngineDto } from '../../../core/dto/device/engine/engine.dto'
import { ResourceName } from '../../../core/ResourceName'
import RelationTab from '../../common/RelationTab'
import relationTabFilter from '../../common/RelationTabFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { NotificationTagDto } from '../../../core/dto/system-notifications/notification-tag.dto'
import { NotificationCategoryDto } from '../../../core/dto/system-notifications/notification-category.dto'

interface NotificationTagTitleProps extends TitleProps {
  record?: NotificationTagDto
}

const ShowTitle = (data: NotificationTagTitleProps) => {
  const { record: tag } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.notification-tags.titles.show')}: {tag?.id}.{' '}
      {tag?.description}
    </span>
  )
}

const GeneralActions = () => {
  const { record: engine } = useShowContext<EngineDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.NOTIFICATION_TAGS}`}
              record={engine}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_NOTIFICATION_TAGS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const NotificationTagTabs = ({ ...props }) => {
  const translate = useTranslate()
  const { record: tag } = useShowContext<NotificationTagDto>()
  const hasAuthority = useHasAuthority()

  const categoriesFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="search" alwaysOn />],
      resource: ResourceName.NOTIFICATION_TAGS,
    })

  const usersFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="userName" alwaysOn />],
      resource: ResourceName.NOTIFICATION_TAGS,
    })

  return (
    <TabbedShowLayout {...props}>
      <Tab label="resources.notification-tags.tabs.general">
        <TextField label="ID" source="id" />
        <TextField source="description" />
        <GeneralActions />
      </Tab>
      <Tab
        path="categories"
        label={translate('resources.notification-tags.tabs.categories')}
      >
        <>
          <RelationTab<NotificationTagDto>
            resource={ResourceName.NOTIFICATION_CATEGORIES}
            source="categoriesIds"
            mode={
              hasAuthority(Authority.EDIT_NOTIFICATION_TAGS) ? 'edit' : 'show'
            }
            attachMethod="attachCategories"
            detachMethod="detachCategories"
            attachRequestPayload={(r, ids) => ({
              notificationTagId: r.id,
              categoriesIds: ids,
            })}
            detachRequestPayload={(r, ids) => ({
              notificationTagId: r.id,
              categoriesIds: ids,
            })}
            refetchListAfterChange={(filters) =>
              filters?.filterNotificationTagId === true
            }
            filters={categoriesFilters() as ReactElement[]}
            filterDefaultValues={{
              notificationTagId: tag?.id,
              filterNotificationTagId: false,
            }}
            sort={{
              field: 'id',
              order: 'DESC',
            }}
          >
            <TextField label="ID" source="id" />
            <FunctionField<NotificationCategoryDto>
              source="name"
              render={(category?: NotificationCategoryDto) =>
                translate(
                  `resources.enums.NotificationCategoryNames.${category?.name}`,
                )
              }
            />
            <TextField source="defaultDescription" />
          </RelationTab>
        </>
      </Tab>
      {hasAuthority(Authority.VIEW_USERS) && (
        <Tab
          path="users"
          label={translate('resources.notification-tags.tabs.users')}
        >
          <>
            <RelationTab<NotificationTagDto>
              resource={ResourceName.USERS}
              source="usersIds"
              mode={
                hasAuthority(Authority.EDIT_NOTIFICATION_TAGS) ? 'edit' : 'show'
              }
              attachMethod="attachUsers"
              detachMethod="detachUsers"
              attachRequestPayload={(r, ids) => ({
                notificationTagId: r.id,
                usersIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                notificationTagId: r.id,
                usersIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterNotificationTagId === true
              }
              filters={usersFilters() as ReactElement[]}
              filterDefaultValues={{
                notificationTagId: tag?.id,
                filterNotificationTagId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="userName" />
            </RelationTab>
          </>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const NotificationTagShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <NotificationTagTabs />
  </Show>
)
